import { useMemo } from 'react';
import { FileText, Image as ImageIcon } from 'lucide-react';
import { apiConfig } from '@/config/apiConfig';
import type { FieldEditMeta, FieldMeta } from '../../types/resource';
import { effectiveUidt, selectOptionsFor, toMultiSelectValues } from '../../utils/fieldValue';
import { SearchOptionInput } from './SearchOptionInput';

interface FieldEditorProps {
  field: FieldMeta;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
  error?: string;
}

const READONLY_UIDT = new Set([
  'ID',
  'Formula',
  'Rollup',
  'Lookup',
  'CreatedTime',
  'LastModifiedTime',
  'CreatedBy',
  'LastModifiedBy',
  'Barcode',
  'QrCode',
]);

export function FieldEditor({ field, value, onChange, disabled, error }: FieldEditorProps) {
  const uidt = effectiveUidt(field);
  const required = field.edit?.required === true;
  const editVia = field.edit_via;
  const readOnly =
    disabled ||
    READONLY_UIDT.has(uidt as string) ||
    field.editable === false ||
    Boolean(editVia);

  const displayType = resolveDisplayType(field);

  const control = useMemo(() => {
    // display_type wins over uidt — that's where the backend puts widget hints.
    if (displayType === 'image_picker') return renderImageGallery(field, value);
    if (displayType === 'search') {
      return renderSearchOption(field, value, onChange, required, Boolean(disabled));
    }
    if (readOnly) return renderReadonly(field, value);
    switch (uidt) {
      case 'LongText':
        return renderTextarea(field, value, onChange, required);
      case 'Number':
      case 'Decimal':
      case 'Currency':
      case 'Percent':
      case 'Rating':
        return renderNumber(field, value, onChange, required);
      case 'Date':
        return renderDate(field, value, onChange, required);
      case 'DateTime':
        return renderDateTime(field, value, onChange, required);
      case 'Time':
        return renderTime(field, value, onChange, required);
      case 'Email':
        return renderTypedInput('email', field, value, onChange, required);
      case 'PhoneNumber':
        return renderTypedInput('tel', field, value, onChange, required);
      case 'URL':
        return renderTypedInput('url', field, value, onChange, required);
      case 'Checkbox':
        return renderCheckbox(field, value, onChange);
      case 'SingleSelect':
        return renderSelect(field, value, onChange, required);
      case 'MultiSelect':
        return renderMultiSelect(field, value, onChange);
      case 'JSON':
      case 'Geometry':
      case 'GeoData':
        return renderJsonTextarea(field, value, onChange, required);
      case 'search':
        return renderSearchOption(field, value, onChange, required, Boolean(disabled));
      case 'Attachment':
        return renderImageGallery(field, value);
      case 'LinkToAnotherRecord':
        return renderUnsupported(field, value);
      default:
        return renderText(field, value, onChange, required);
    }
  }, [field, value, onChange, readOnly, uidt, required, disabled, displayType]);

  const label = field.display_title || field.title;

  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-rgf-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {control}
      {editVia && (
        <p className="text-xs text-rgf-gray-500">
          Read-only — this value comes from a linked record. Edit via{' '}
          <code className="font-mono">{editVia}</code> if the meta exposes it as
          its own editable field.
        </p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

const INPUT_CLASS =
  'w-full rounded-lg border border-rgf-gray-300 px-3 py-2 text-sm text-rgf-gray-900 focus:border-rgf-primary-500 focus:outline-none focus:ring-1 focus:ring-rgf-primary-500 disabled:cursor-not-allowed disabled:bg-rgf-gray-50 disabled:text-rgf-gray-500';

function renderReadonly(_field: FieldMeta, value: unknown) {
  const stringValue = value === null || value === undefined || value === '' ? '—' : String(value);
  return (
    <div className={INPUT_CLASS + ' bg-rgf-gray-50 text-rgf-gray-600'}>
      {stringValue}
    </div>
  );
}

function renderText(_field: FieldMeta, value: unknown, onChange: (v: unknown) => void, required: boolean) {
  return (
    <input
      type="text"
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={INPUT_CLASS}
    />
  );
}

function renderTextarea(_field: FieldMeta, value: unknown, onChange: (v: unknown) => void, required: boolean) {
  return (
    <textarea
      rows={4}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={INPUT_CLASS + ' font-normal'}
    />
  );
}

function renderNumber(_field: FieldMeta, value: unknown, onChange: (v: unknown) => void, required: boolean) {
  return (
    <input
      type="number"
      inputMode="decimal"
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={INPUT_CLASS}
    />
  );
}

function renderDate(_field: FieldMeta, value: unknown, onChange: (v: unknown) => void, required: boolean) {
  const asDate = typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : '';
  return (
    <input
      type="date"
      value={asDate}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={INPUT_CLASS}
    />
  );
}

function renderDateTime(_field: FieldMeta, value: unknown, onChange: (v: unknown) => void, required: boolean) {
  const asDateTime = toDateTimeLocal(value);
  return (
    <input
      type="datetime-local"
      value={asDateTime}
      onChange={(e) => onChange(e.target.value ? `${e.target.value}:00` : '')}
      required={required}
      className={INPUT_CLASS}
    />
  );
}

function renderTime(_field: FieldMeta, value: unknown, onChange: (v: unknown) => void, required: boolean) {
  return (
    <input
      type="time"
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={INPUT_CLASS}
    />
  );
}

function renderTypedInput(
  type: 'email' | 'tel' | 'url',
  _field: FieldMeta,
  value: unknown,
  onChange: (v: unknown) => void,
  required: boolean,
) {
  return (
    <input
      type={type}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={INPUT_CLASS}
    />
  );
}

function renderCheckbox(field: FieldMeta, value: unknown, onChange: (v: unknown) => void) {
  const checked = value === true || value === 'true' || value === 1;
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-rgf-gray-300 text-rgf-primary-600 focus:ring-rgf-primary-500"
      />
      <span className="text-rgf-gray-700">{field.display_title || field.title}</span>
    </label>
  );
}

function renderSelect(field: FieldMeta, value: unknown, onChange: (v: unknown) => void, required: boolean) {
  const options = selectOptionsFor(field);
  return (
    <select
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={INPUT_CLASS}
    >
      <option value="">— Select —</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function renderMultiSelect(field: FieldMeta, value: unknown, onChange: (v: unknown) => void) {
  const options = selectOptionsFor(field);
  const selected = new Set(toMultiSelectValues(value));
  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(Array.from(next));
  };
  return (
    <div className="flex flex-wrap gap-1.5 rounded-lg border border-rgf-gray-300 p-2">
      {options.map((o) => {
        const isOn = selected.has(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => toggle(o.value)}
            className={
              'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition ' +
              (isOn
                ? 'border-rgf-primary-500 bg-rgf-primary-50 text-rgf-primary-700'
                : 'border-rgf-gray-200 bg-white text-rgf-gray-600 hover:border-rgf-gray-300')
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function renderJsonTextarea(_field: FieldMeta, value: unknown, onChange: (v: unknown) => void, required: boolean) {
  const asString =
    value === null || value === undefined
      ? ''
      : typeof value === 'string'
        ? value
        : JSON.stringify(value, null, 2);
  return (
    <textarea
      rows={5}
      value={asString}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={INPUT_CLASS + ' font-mono text-xs'}
    />
  );
}

function renderSearchOption(
  field: FieldMeta,
  value: unknown,
  onChange: (v: unknown) => void,
  required: boolean,
  disabled: boolean,
) {
  // Backend puts search_meta at either the field root or under `edit`.
  const searchMeta = field.edit?.search_meta ?? field.search_meta;
  if (!searchMeta) return renderText(field, value, onChange, required);
  const editMeta: FieldEditMeta = field.edit ?? { search_meta: searchMeta };
  return (
    <SearchOptionInput
      editMeta={editMeta.search_meta ? editMeta : { ...editMeta, search_meta: searchMeta }}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
    />
  );
}

// display_type lives at field root or under `edit`, depending on the widget.
function resolveDisplayType(field: FieldMeta): string | undefined {
  const raw = field.display_type ?? field.edit?.display_type;
  return typeof raw === 'string' && raw !== '' ? raw : undefined;
}

interface AttachmentEntry {
  url: string;
  mimetype?: string;
  title?: string;
}

// NocoDB attachment URLs are relative; prefix with assetBaseURL unless already absolute.
function resolveAssetUrl(url: string): string {
  if (!url) return url;
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return url; // http:, https:, data:, …
  if (url.startsWith('//')) return url;
  const base = String(apiConfig.assetBaseURL ?? '').replace(/\/$/, '');
  const path = url.replace(/^\//, '');
  return base ? `${base}/${path}` : path;
}

// Accepts a bare URL string, `{url,…}` object, or NocoDB array shape.
function normaliseAttachmentList(value: unknown): AttachmentEntry[] {
  if (value === null || value === undefined || value === '') return [];
  if (typeof value === 'string') return [{ url: value }];
  if (Array.isArray(value)) {
    return value.flatMap((entry) => normaliseAttachmentList(entry));
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const url =
      typeof obj.url === 'string'
        ? obj.url
        : typeof obj.path === 'string'
          ? obj.path
          : undefined;
    if (!url) return [];
    return [
      {
        url,
        mimetype: typeof obj.mimetype === 'string' ? obj.mimetype : undefined,
        title:
          typeof obj.title === 'string'
            ? obj.title
            : typeof obj.filename === 'string'
              ? obj.filename
              : undefined,
      },
    ];
  }
  return [];
}

function renderImageGallery(_field: FieldMeta, value: unknown) {
  const entries = normaliseAttachmentList(value);
  if (entries.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed border-rgf-gray-300 bg-rgf-gray-50 px-3 py-4 text-xs text-rgf-gray-500">
        <ImageIcon className="h-4 w-4" />
        <span>No attachments.</span>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2 rounded-lg border border-rgf-gray-200 bg-rgf-gray-50 p-2">
      {entries.map((entry, i) => {
        const resolved = resolveAssetUrl(entry.url);
        const isImage = isImageEntry(entry);
        return (
          <a
            key={`${entry.url}-${i}`}
            href={resolved}
            target="_blank"
            rel="noreferrer"
            className="group block w-24 rounded-md border border-rgf-gray-200 bg-white p-1 text-center hover:border-rgf-primary-300"
            title={entry.title ?? entry.url}
          >
            {isImage ? (
              <img
                src={resolved}
                alt={entry.title ?? `Attachment ${i + 1}`}
                className="h-20 w-full rounded object-cover"
                loading="lazy"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div className="flex h-20 w-full items-center justify-center rounded bg-rgf-gray-100 text-rgf-gray-500">
                <FileText className="h-6 w-6" />
              </div>
            )}
            <p className="mt-1 truncate text-[10px] text-rgf-gray-600 group-hover:text-rgf-primary-700">
              {entry.title ?? `#${i + 1}`}
            </p>
          </a>
        );
      })}
    </div>
  );
}

function isImageEntry(entry: AttachmentEntry): boolean {
  if (entry.mimetype && entry.mimetype.toLowerCase().startsWith('image/')) return true;
  const url = (entry.url || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg|avif|heic|heif)(\?|$)/i.test(url);
}

function renderUnsupported(field: FieldMeta, value: unknown) {
  const uidt = field.edit?.uidt ?? field.type ?? 'unknown';
  const summary =
    Array.isArray(value)
      ? `${value.length} item${value.length === 1 ? '' : 's'}`
      : value && typeof value === 'object'
        ? JSON.stringify(value)
        : value == null || value === ''
          ? '—'
          : String(value);
  return (
    <div className={INPUT_CLASS + ' bg-rgf-gray-50 text-rgf-gray-600'}>
      <div className="text-xs italic text-rgf-gray-500">
        Read-only for <code className="font-mono">{uidt}</code> widget in this release.
      </div>
      <div className="mt-1 truncate">{summary}</div>
    </div>
  );
}

function toDateTimeLocal(value: unknown): string {
  if (typeof value !== 'string' || value === '') return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
